import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';

import { SERVER_PROVIDER_ID, providerPurpose } from '../../core/providers';
import { listProviders } from '../../core/repositories/providerRepo';
import type { Preset, ProviderConfig, QuietPeriod } from '../../core/types';
import { newId } from '../../core/ids';
import { useFeatureFlags } from '../../core/featureFlags';
import { loadTtsCatalog } from '../mimo/catalog';
import type { TtsCatalogModel } from '../mimo/catalog';
import { loadOfficialCatalog } from '../chat/officialModelCatalog';
import type { OfficialImageOption } from '../chat/officialModelCatalog';
import { useProviderModels } from '../chat/useProviderModels';
import { useEntitlements } from '../subscription/entitlements';
import {
  FramedBox,
  FramedButton,
  FramedHeading,
  FramedInput,
  FramedSelect,
  FramedSwitch,
  FramedText,
} from './Framed';
import { automationEditorHeight, automationEditorMeta, roleVoiceAutoDesignMeta, roleVoiceEditorHeight } from './automationLayout';
import { roleVoiceMode, roleVoiceProviderError } from '../mimo/roleVoiceStore';
import { RoleVoiceEditor } from './RoleVoiceEditor';

const AUTOMATION_LABELS = {
  reply_only: '仅回复消息',
  auto_bubble: '自动冒泡',
  auto_moment: '自动朋友圈',
  auto_bubble_moment: '自动冒泡 + 朋友圈',
} as const;

interface Props {
  draft: Preset;
  top: number;
  onChange: (next: Preset) => void;
}

export function PresetAutomationEditor({ draft, top, onChange }: Props): ReactElement {
  const flags = useFeatureFlags();
  const visibility = { image: flags.imageInitiative.enabled, tts: flags.ttsInitiative.enabled };
  const imageOffset = visibility.image ? 0 : 74;
  const voiceHeight = roleVoiceEditorHeight(draft);
  const voiceProviderError = roleVoiceProviderError(draft);
  const autoVoiceDesignMeta = roleVoiceAutoDesignMeta(top, visibility);
  const quietOffset = imageOffset + (visibility.tts ? 0 : 76) - voiceHeight;
  const componentMeta = automationEditorMeta(top, automationEditorHeight(draft, visibility), visibility);
  const entitlements = useEntitlements();
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [officialImages, setOfficialImages] = useState<OfficialImageOption[]>([]);
  const [ttsModels, setTtsModels] = useState<TtsCatalogModel[]>([]);
  const [loadNotice, setLoadNotice] = useState<string | null>(null);
  const [planNotice, setPlanNotice] = useState<string | null>(null);

  useEffect(() => {
    const selected = draft.ttsProviderId === SERVER_PROVIDER_ID ? ttsModels.find(model => model.id === draft.ttsModel) : undefined;
    if (selected && draft.ttsVoiceMode !== selected.mode) onChange({ ...draft, ttsVoiceMode: selected.mode });
  }, [draft, onChange, ttsModels]);

  useEffect(() => {
    let alive = true;
    void Promise.allSettled([listProviders(), loadOfficialCatalog(), loadTtsCatalog()]).then(([providerResult, catalogResult, ttsResult]) => {
      if (!alive) return;
      if (providerResult.status === 'fulfilled') setProviders(providerResult.value);
      if (catalogResult.status === 'fulfilled') setOfficialImages(catalogResult.value.image);
      if (ttsResult.status === 'fulfilled') setTtsModels(ttsResult.value.filter((model) => model.available));
      if ([providerResult, catalogResult, ttsResult].some((result) => result.status === 'rejected')) {
        setLoadNotice('部分媒体模型暂未加载；仍可保存已有配置。');
      }
    });
    return () => { alive = false; };
  }, []);

  const imageProviders = providers.filter((provider) => provider.enabled && providerPurpose(provider) === 'image');
  const ttsProviders = providers.filter((provider) => provider.enabled && providerPurpose(provider) === 'tts');
  const selectedImageProvider = imageProviders.find((provider) => provider.id === draft.imageProviderId) ?? null;
  const selectedTtsProvider = ttsProviders.find((provider) => provider.id === draft.ttsProviderId) ?? null;
  const imageProviderCatalog = useProviderModels(selectedImageProvider);
  const ttsProviderCatalog = useProviderModels(selectedTtsProvider);
  const patch = (portion: Partial<Preset>): void => onChange({ ...draft, ...portion });
  const mode = draft.automationMode ?? 'reply_only';
  const autoText = entitlements.active && entitlements.capabilities.autoText;
  const autoImageAllowed = entitlements.active && entitlements.capabilities.autoImage;
  const autoTtsAllowed = entitlements.active && entitlements.capabilities.autoTts;
  const quietPeriods = draft.quietPeriods ?? [];
  // Only warn about capabilities the role actually wants to use. A plan that
  // supports text automation but not image/TTS must not make "自动冒泡" or
  // "自动朋友圈" look unsupported.
  const automationNeedsText = mode !== 'reply_only';
  const showUnsupportedNotice = (automationNeedsText && !autoText)
    || (draft.autoImageEnabled === true && !autoImageAllowed)
    || (draft.autoTtsEnabled === true && !autoTtsAllowed)
    || planNotice !== null;
  const rejectUnsupportedFeature = (): void => {
    setPlanNotice('当前套餐不包含该功能。');
  };

  const uniqueModels = (...values: readonly (string | null | undefined)[]): string[] =>
    [...new Set(values.map((value) => value?.trim() ?? '').filter((value) => value !== ''))];
  const imageModelOptions: string[] = draft.imageProviderId === SERVER_PROVIDER_ID
    ? officialImages.map((option) => option.slug)
    : uniqueModels(selectedImageProvider?.defaultModel, ...imageProviderCatalog.models, draft.imageModel);
  const ttsModelOptions: string[] = draft.ttsProviderId === SERVER_PROVIDER_ID
    ? ttsModels.map((model) => model.id)
    : uniqueModels(selectedTtsProvider?.defaultModel, ...ttsProviderCatalog.models, draft.ttsModel);

  const addQuietPeriod = (): void => {
    const period: QuietPeriod = { id: newId(), start: '23:00', end: '07:00' };
    patch({ quietPeriods: [...quietPeriods, period] });
  };

  const patchQuietPeriod = (id: string, change: Partial<QuietPeriod>): void => {
    patch({ quietPeriods: quietPeriods.map((period) => period.id === id ? { ...period, ...change } : period) });
  };

  return (
    <>
      <FramedBox
        meta={componentMeta.card}
        className="zs-card"
      >
        <FramedHeading
          level={2}
          meta={componentMeta.title}
          className="zs-section-title"
        >自动化与媒体</FramedHeading>

        <FramedText meta={{ id: 'presets.editor.automation.planState', page: 'presets', role: 'text', label: '套餐能力状态', layout: { x: 20, y: top + 39, width: 342, height: 34 }, zIndex: 6 }} className="zs-entry-rules">
          {entitlements.active
            ? `当前套餐：自动生文${autoText ? '可用' : '不可用'} · 自动生图${autoImageAllowed ? '可用' : '不可用'} · 自动语音${autoTtsAllowed ? '可用' : '不可用'}`
            : '当前无有效官方套餐：只能使用「仅回复消息」，自动生图与自动语音不可启用。'}
        </FramedText>

        <FramedText meta={{ id: 'presets.editor.automation.modeLabel', page: 'presets', role: 'text', label: '自动化模式标签', layout: { x: 20, y: top + 78, width: 160, height: 16 }, zIndex: 6 }} className="zs-field-label">自动化</FramedText>
        <FramedSelect
          meta={{ id: 'presets.editor.automation.mode', page: 'presets', role: 'input', label: '自动化模式', layout: { x: 20, y: top + 96, width: 342, height: 40 }, zIndex: 6 }}
          className="zs-input"
          value={autoText ? mode : 'reply_only'}
          onValueChange={(value, event) => {
            if (value !== 'reply_only' && !autoText) {
              // React does not reapply an unchanged controlled `value` after a
              // rejected native select change, so reset the DOM value itself.
              event.currentTarget.value = 'reply_only';
              rejectUnsupportedFeature();
              return;
            }
            setPlanNotice(null);
            patch({ automationMode: value as Preset['automationMode'] });
          }}
        >
          {(Object.entries(AUTOMATION_LABELS) as [keyof typeof AUTOMATION_LABELS, string][]).map(([value, label]) => (
            <option key={value} value={value}>{label}{value !== 'reply_only' && !autoText ? '（套餐不包含）' : ''}</option>
          ))}
        </FramedSelect>

        <FramedText meta={{ id: 'presets.editor.automation.imageLabel', page: 'presets', role: 'text', label: '自动生图标签', layout: { x: 20, y: top + 146, width: 250, height: 24 }, zIndex: 6 }} className="zs-field-label">允许角色按需求自动生图</FramedText>
        <FramedSwitch
          meta={{ id: 'presets.editor.automation.imageSwitch', page: 'presets', role: 'button', label: '允许自动生图', layout: { x: 300, y: top + 142, width: 62, height: 32 }, zIndex: 6, props: ['checked', 'aria-disabled'] }}
          className={`zs-switch zs-media-switch${draft.autoImageEnabled && autoImageAllowed ? ' zs-switch-on' : ''}${autoImageAllowed ? '' : ' zs-switch-locked'}`}
          checked={draft.autoImageEnabled === true && autoImageAllowed}
          ariaDisabled={!autoImageAllowed}
          onToggle={(next) => {
            if (next && !autoImageAllowed) {
              rejectUnsupportedFeature();
              return;
            }
            setPlanNotice(null);
            patch({ autoImageEnabled: next });
          }}
        >
          <span aria-hidden="true" className="zs-media-switch-thumb" />
        </FramedSwitch>
        <FramedSelect
          meta={{ id: 'presets.editor.automation.imageProvider', page: 'presets', role: 'input', label: '生图提供商', layout: { x: 20, y: top + 174, width: 164, height: 40 }, zIndex: 6 }}
          className="zs-input"
          value={draft.imageProviderId ?? ''}
          disabled={!autoImageAllowed || !draft.autoImageEnabled}
          onValueChange={(value) => {
            const firstModel = value === SERVER_PROVIDER_ID
              ? officialImages[0]?.slug ?? ''
              : imageProviders.find((provider) => provider.id === value)?.defaultModel ?? '';
            patch({ imageProviderId: value, imageModel: firstModel });
          }}
        >
          <option value="">选择生图提供商</option>
          {officialImages.length > 0 ? <option value={SERVER_PROVIDER_ID}>官方生图</option> : null}
          {imageProviders.map((provider) => <option key={provider.id} value={provider.id}>{provider.name}</option>)}
        </FramedSelect>
        <FramedSelect
          meta={{ id: 'presets.editor.automation.imageModel', page: 'presets', role: 'input', label: '生图模型', layout: { x: 194, y: top + 174, width: 168, height: 40 }, zIndex: 6 }}
          className="zs-input"
          value={draft.imageModel ?? ''}
          disabled={!autoImageAllowed || !draft.autoImageEnabled || draft.imageProviderId === undefined}
          onValueChange={(value) => patch({ imageModel: value })}
        >
          <option value="">选择生图模型</option>
          {imageModelOptions.map((model) => <option key={model} value={model}>{model}</option>)}
        </FramedSelect>

        {visibility.image ? <>
        <FramedText meta={componentMeta.imagePolicyLabel} className="zs-field-label">生图积极性</FramedText>
        <FramedSelect
          meta={componentMeta.imagePolicy}
          className="zs-input"
          value={draft.imageGenerationPolicy ?? 'balanced'}
          disabled={!autoImageAllowed || !draft.autoImageEnabled}
          onValueChange={(value) => patch({ imageGenerationPolicy: value as Preset['imageGenerationPolicy'] })}
        >
          <option value="user_priority">主用户要求</option>
          <option value="character_priority">主角色依据自身设定决定</option>
          <option value="balanced">角色决定或用户要求</option>
        </FramedSelect>
        </> : null}

        <FramedText meta={{ id: 'presets.editor.automation.ttsLabel', page: 'presets', role: 'text', label: '自动语音标签', layout: { x: 20, y: top + 300 - imageOffset, width: 250, height: 24 }, zIndex: 6 }} className="zs-field-label">允许角色按需求发送语音</FramedText>
        <FramedSwitch
          meta={{ id: 'presets.editor.automation.ttsSwitch', page: 'presets', role: 'button', label: '允许自动语音', layout: { x: 300, y: top + 296 - imageOffset, width: 62, height: 32 }, zIndex: 6, props: ['checked', 'aria-disabled'] }}
          className={`zs-switch zs-media-switch${draft.autoTtsEnabled && autoTtsAllowed ? ' zs-switch-on' : ''}${autoTtsAllowed ? '' : ' zs-switch-locked'}`}
          checked={draft.autoTtsEnabled === true && autoTtsAllowed}
          ariaDisabled={!autoTtsAllowed}
          onToggle={(next) => {
            if (next && !autoTtsAllowed) {
              rejectUnsupportedFeature();
              return;
            }
            setPlanNotice(null);
            patch({ autoTtsEnabled: next });
          }}
        >
          <span aria-hidden="true" className="zs-media-switch-thumb" />
        </FramedSwitch>
        <FramedSelect
          meta={{ id: 'presets.editor.automation.ttsProvider', page: 'presets', role: 'input', label: '语音提供商', layout: { x: 20, y: top + 328 - imageOffset, width: 164, height: 40 }, zIndex: 6 }}
          className="zs-input"
          value={draft.ttsProviderId ?? ''}
          disabled={!autoTtsAllowed || !draft.autoTtsEnabled}
          onValueChange={(value) => {
            const firstModel = value === SERVER_PROVIDER_ID
              ? ttsModels[0]?.id ?? ''
              : ttsProviders.find((provider) => provider.id === value)?.defaultModel ?? '';
            patch({ ttsProviderId: value, ttsModel: firstModel, ttsVoiceMode: value === SERVER_PROVIDER_ID ? ttsModels.find(model => model.id === firstModel)?.mode : undefined });
          }}
        >
          <option value="">选择语音提供商</option>
          {ttsModels.length > 0 ? <option value={SERVER_PROVIDER_ID}>官方语音</option> : null}
          {ttsProviders.map((provider) => <option key={provider.id} value={provider.id}>{provider.name}</option>)}
        </FramedSelect>
        <FramedSelect
          meta={{ id: 'presets.editor.automation.ttsModel', page: 'presets', role: 'input', label: '语音模型', layout: { x: 194, y: top + 328 - imageOffset, width: 168, height: 40 }, zIndex: 6 }}
          className="zs-input"
          value={draft.ttsModel ?? ''}
          disabled={!autoTtsAllowed || !draft.autoTtsEnabled || draft.ttsProviderId === undefined}
          onValueChange={(value) => patch({ ttsModel: value, ttsVoiceMode: draft.ttsProviderId === SERVER_PROVIDER_ID ? ttsModels.find(model => model.id === value)?.mode : undefined })}
        >
          <option value="">选择语音模型</option>
          {ttsModelOptions.map((model) => {
            const label = ttsModels.find((item) => item.id === model)?.label ?? model;
            const unsupported = draft.ttsProviderId !== SERVER_PROVIDER_ID && roleVoiceMode({ ttsModel: model }) !== 'preset';
            return <option key={model} value={model} disabled={unsupported}>{label}{unsupported ? '（不支持）' : ''}</option>;
          })}
        </FramedSelect>

        {visibility.tts ? <>
        <FramedText meta={componentMeta.ttsPolicyLabel} className="zs-field-label">语音 TTS 积极性</FramedText>
        <FramedSelect
          meta={componentMeta.ttsPolicy}
          className="zs-input"
          value={draft.ttsGenerationPolicy ?? 'balanced'}
          disabled={!autoTtsAllowed || !draft.autoTtsEnabled}
          onValueChange={(value) => patch({ ttsGenerationPolicy: value as Preset['ttsGenerationPolicy'] })}
        >
          <option value="user_priority">主用户要求</option>
          <option value="character_priority">主角色依据自身设定决定</option>
          <option value="balanced">角色决定或用户要求</option>
        </FramedSelect>
        </> : null}

        {voiceHeight > 0 ? <>
          {!voiceProviderError ? <>
            <FramedText meta={autoVoiceDesignMeta.label} className="zs-field-label">自动音色设计</FramedText>
            <FramedSwitch meta={autoVoiceDesignMeta.switch}
              className={`zs-switch zs-media-switch${draft.ttsAutoVoiceDesign ? ' zs-switch-on' : ''}`}
              checked={draft.ttsAutoVoiceDesign === true} onToggle={next => patch({ ttsAutoVoiceDesign: next })}>
              <span aria-hidden="true" className="zs-media-switch-thumb" />
            </FramedSwitch>
          </> : null}
          <FramedBox meta={{ id: 'presets.editor.automation.roleVoice', page: 'presets', role: 'container', label: '角色专属音色', layout: { x: 20, y: top + 500 - imageOffset - (visibility.tts ? 0 : 76), width: 342, height: voiceHeight - 56 }, zIndex: 6 }}>
            {voiceProviderError ? <p role="alert" className="zs-role-voice-error">{voiceProviderError}</p> : <RoleVoiceEditor key={draft.id} presetId={draft.id} mode={roleVoiceMode(draft)} autoVoiceDesign={draft.ttsAutoVoiceDesign === true} />}
          </FramedBox>
        </> : null}

        <FramedText meta={{ id: 'presets.editor.automation.quietTitle', page: 'presets', role: 'text', label: '勿扰时段标题', layout: { x: 20, y: top + 456 - quietOffset, width: 220, height: 24 }, zIndex: 6 }} className="zs-field-label">冒泡勿扰时段（可无限添加）</FramedText>
        <FramedText meta={{ id: 'presets.editor.automation.quietHint', page: 'presets', role: 'text', label: '勿扰时段说明', layout: { x: 20, y: top + 480 - quietOffset, width: 342, height: 30 }, zIndex: 6 }} className="zs-entry-rules">支持跨午夜；勿扰期间不自动冒泡，但正常回复和朋友圈不受影响。</FramedText>

        {quietPeriods.map((period, index) => {
          const rowTop = top + 516 - quietOffset + index * 52;
          return (
            <div key={period.id}>
              <FramedInput
                meta={{ id: `presets.editor.automation.quiet.${index}.start`, page: 'presets', role: 'input', label: `勿扰开始 ${index + 1}`, layout: { x: 20, y: rowTop, width: 132, height: 40 }, zIndex: 6 }}
                className="zs-input"
                type="time"
                value={period.start}
                onValueChange={(value) => patchQuietPeriod(period.id, { start: value })}
              />
              <FramedInput
                meta={{ id: `presets.editor.automation.quiet.${index}.end`, page: 'presets', role: 'input', label: `勿扰结束 ${index + 1}`, layout: { x: 160, y: rowTop, width: 132, height: 40 }, zIndex: 6 }}
                className="zs-input"
                type="time"
                value={period.end}
                onValueChange={(value) => patchQuietPeriod(period.id, { end: value })}
              />
              <FramedButton
                meta={{ id: `presets.editor.automation.quiet.${index}.remove`, page: 'presets', role: 'button', label: `删除勿扰时段 ${index + 1}`, layout: { x: 300, y: rowTop, width: 62, height: 40 }, zIndex: 6 }}
                className="zs-button zs-button-danger"
                onClick={() => patch({ quietPeriods: quietPeriods.filter((item) => item.id !== period.id) })}
              >删除</FramedButton>
            </div>
          );
        })}
        <FramedButton
          meta={{ id: 'presets.editor.automation.quiet.add', page: 'presets', role: 'button', label: '添加勿扰时段', layout: { x: 20, y: top + 516 - quietOffset + quietPeriods.length * 52, width: 342, height: 40 }, zIndex: 6 }}
          className="zs-button zs-button-ghost"
          onClick={addQuietPeriod}
        >＋ 添加勿扰时段</FramedButton>
        {showUnsupportedNotice ? (
          <FramedText meta={{ id: 'presets.editor.automation.upsell', page: 'presets', role: 'text', label: '套餐不支持提示', layout: { x: 20, y: top + 564 - quietOffset + quietPeriods.length * 52, width: 342, height: 44 }, zIndex: 6 }} className="zs-import-notice">
            {planNotice ?? '当前套餐不包含该功能。请到「设置 → 官方订阅」查看或购买包含该功能的套餐。'}
          </FramedText>
        ) : null}
        {loadNotice ? (
          <FramedText meta={{ id: 'presets.editor.automation.loadNotice', page: 'presets', role: 'text', label: '媒体配置加载提示', layout: { x: 20, y: top + 612 - quietOffset + quietPeriods.length * 52, width: 342, height: 34 }, zIndex: 6 }} className="zs-entry-rules">{loadNotice}</FramedText>
        ) : null}
      </FramedBox>
    </>
  );
}
